import React, { useState } from "react";
import { Button, Modal, ModalHeader, ModalBody, ModalFooter } from "reactstrap";
import Select from "react-select";
import NavHeader from "components/torneos/NavHeader";
import ScoreStepper from "components/torneos/ScoreStepper";
import { genId } from "components/torneos/ids";
import { GOLES_WO, alternarWO, daWO, esWODoble, ladoWO, marcadorDePartido } from "components/torneos/resultado";
import { porCanchaYHora } from "components/torneos/horarios";
import { toastBigError, toastConfirm } from "components/utils/toasts";
import StandingsTable from "./StandingsTable";
import { generarResultadosFecha } from "./resultadosFechaPdf";

// Una fila de partido. El resultado se edita en estado LOCAL y recién persiste
// (onChange) al tocar "Guardar resultado". Un partido no jugado no muestra marcador.
function MatchResultRow({ match, teams, attendance, onChange }) {
  const [editing, setEditing] = useState(false);
  const [home, setHome] = useState([]);
  const [away, setAway] = useState([]);
  const [wo, setWo] = useState(""); // "" | "home" | "away" | "both" — quién da walk-over
  const [goleadoresSide, setGoleadoresSide] = useState(null); // null | "home" | "away"

  const getTeamById = (teamId) => teams.find((t) => t.id === teamId);
  const teamName = (teamId) => {
    const t = getTeamById(teamId);
    return t ? t.name : "—";
  };
  const getAttendingPlayers = (teamId) => {
    const team = getTeamById(teamId);
    if (!team) return [];
    const ids = attendance[teamId] || [];
    return team.players.filter((p) => ids.includes(p.id));
  };

  const bothTeams = match.homeTeamId && match.awayTeamId;
  const marcador = marcadorDePartido(match);

  const startEdit = () => {
    setHome(match.homeGoals || []);
    setAway(match.awayGoals || []);
    setWo(ladoWO(match) || "");
    setGoleadoresSide(null);
    setEditing(true);
  };
  const cancelar = () => {
    setGoleadoresSide(null);
    setEditing(false);
  };
  // Un partido con WO no tiene goleadores: el 3 a 0 se deriva del WO al mostrarlo.
  const guardar = () => {
    onChange({
      ...match,
      jugado: true,
      wo,
      homeGoals: wo ? [] : home,
      awayGoals: wo ? [] : away,
    });
    setGoleadoresSide(null);
    setEditing(false);
  };
  // Quitar el resultado descarta lo cargado (marcador, goleadores y WO) y el partido
  // vuelve a contar como no jugado en la tabla de posiciones: se confirma antes.
  const quitar = () =>
    toastConfirm(
      "¿ Quitar el resultado ?",
      teamName(match.homeTeamId) + " vs " + teamName(match.awayTeamId) + ". El partido vuelve a quedar sin jugar.",
      () => onChange({ ...match, jugado: false, wo: "", homeGoals: [], awayGoals: [] })
    );

  // Marcar el WO de un lado (o desmarcarlo) mientras se edita. Los dos lados
  // pueden estar marcados: ahí el partido no cuenta como jugado.
  const toggleWO = (side) => {
    setWo((actual) => alternarWO(actual, side));
    setGoleadoresSide(null);
  };

  // Operaciones sobre el estado local (no persisten hasta Guardar).
  const localFor = (side) => (side === "home" ? [home, setHome] : [away, setAway]);
  const addGoal = (side) => {
    const [arr, set] = localFor(side);
    set([...arr, { id: genId("goal"), playerId: "", minute: "" }]);
  };
  const removeLastGoal = (side) => {
    const [arr, set] = localFor(side);
    if (arr.length === 0) return;
    set(arr.slice(0, -1));
  };
  const removeGoal = (side, goalId) => {
    const [arr, set] = localFor(side);
    set(arr.filter((g) => g.id !== goalId));
  };
  const updateGoal = (side, goalId, field, value) => {
    const [arr, set] = localFor(side);
    set(arr.map((g) => (g.id === goalId ? { ...g, [field]: value } : g)));
  };

  // Editor de goleadores de un equipo (va dentro del modal).
  const renderGoalEditor = (side) => {
    const teamId = side === "home" ? match.homeTeamId : match.awayTeamId;
    const [goals] = localFor(side);
    const playerOptions = getAttendingPlayers(teamId).map((p) => ({
      value: p.id,
      label: (p.camiseta ? "#" + p.camiseta + " " : "") + (p.name || "Sin Nombre"),
    }));
    return (
      <div>
        <div className="d-flex align-items-center justify-content-end mb-2">
          <Button className="btn-arhock btn-sm my-0" onClick={() => addGoal(side)}>
            + Gol
          </Button>
        </div>
        {goals.length === 0 ? (
          <p className="text-muted mb-0" style={{ fontSize: "0.8125rem" }}>
            Sin goleadores cargados. Agregá los goles con "+ Gol".
          </p>
        ) : (
          goals.map((goal) => (
            <div key={goal.id} className="goal-entry">
              <div style={{ flex: 1, minWidth: 0 }}>
                <Select
                  isSearchable={true}
                  placeholder="Goleador..."
                  classNamePrefix="react-select"
                  name={"goal-" + goal.id}
                  value={playerOptions.find((o) => o.value === goal.playerId) || null}
                  onChange={(option) => updateGoal(side, goal.id, "playerId", option ? option.value : "")}
                  options={playerOptions}
                  noOptionsMessage={() => "Sin jugadores en la lista de fe"}
                />
              </div>
              {/* Minuto del gol: oculto por ahora (se reactivará en una etapa
                  posterior). No borrar: el estado goal.minute se sigue manejando. */}
              <input
                type="text"
                inputMode="numeric"
                pattern="[0-9]*"
                className="form-control"
                placeholder="Min"
                value={goal.minute}
                onChange={(e) => updateGoal(side, goal.id, "minute", e.target.value)}
                style={{ width: "64px", height: "44px", textAlign: "center", display: "none" }}
              />
              <span style={{ fontSize: "0.75rem", color: "#9ca3af", display: "none" }}>{"'"}</span>
              <button type="button" className="drill-row-delete" aria-label="Eliminar gol" onClick={() => removeGoal(side, goal.id)}>
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <line x1="18" y1="6" x2="6" y2="18" />
                  <line x1="6" y1="6" x2="18" y2="18" />
                </svg>
              </button>
            </div>
          ))
        )}
      </div>
    );
  };

  const goleadoresTeamId = goleadoresSide === "home" ? match.homeTeamId : match.awayTeamId;

  return (
    <div className="card mb-2 match-card">
      <div className="card-body py-2 px-3">
        <div className="d-flex align-items-center justify-content-between">
          <span className="font-weight-bold" style={{ fontSize: "0.9375rem" }}>
            {match.hora ? match.hora + " · " : ""}
            {teamName(match.homeTeamId)} vs {teamName(match.awayTeamId)}
          </span>
          {match.jugado && !editing && (
            <span className="d-flex align-items-center" style={{ gap: "0.35rem" }}>
              {ladoWO(match) && (
                <span className="badge badge-dark my-0" style={{ fontSize: "0.6875rem" }}>
                  {esWODoble(match) ? "DWO" : "WO"}
                </span>
              )}
              {!esWODoble(match) && (
                <span className="badge badge-success my-0" style={{ fontSize: "0.6875rem" }}>
                  {marcador.local} - {marcador.visitante}
                </span>
              )}
            </span>
          )}
        </div>

        {!bothTeams ? (
          <p className="text-muted mb-0 mt-2" style={{ fontSize: "0.8125rem" }}>
            Faltan cargar los equipos de este partido.
          </p>
        ) : editing ? (
          <>
            <ScoreStepper
              homeLabel={teamName(match.homeTeamId)}
              awayLabel={teamName(match.awayTeamId)}
              homeCount={wo ? (daWO(wo, "home") ? 0 : GOLES_WO) : home.length}
              awayCount={wo ? (daWO(wo, "away") ? 0 : GOLES_WO) : away.length}
              onAdd={addGoal}
              onRemove={removeLastGoal}
              onGoleadores={setGoleadoresSide}
              onWO={toggleWO}
              wo={wo}
              disabled={!!wo}
            />
            {wo && (
              <p className="text-muted mb-0 mt-1 text-center" style={{ fontSize: "0.8125rem" }}>
                {wo === "both"
                  ? "DWO: walk-over de los dos equipos. El partido lo pierden los dos y cada uno recibe " +
                    GOLES_WO +
                    " goles en contra."
                  : teamName(wo === "home" ? match.homeTeamId : match.awayTeamId) +
                    " da walk-over: pierde " +
                    GOLES_WO +
                    " a 0."}
              </p>
            )}
            <div className="d-flex mt-3" style={{ gap: "0.5rem" }}>
              <Button className="btn-arhock btn-sm my-0" onClick={guardar}>
                Guardar resultado
              </Button>
              <Button className="btn-arhock btn-sm my-0" onClick={cancelar}>
                Cancelar
              </Button>
            </div>

            <Modal isOpen={!!goleadoresSide} toggle={() => setGoleadoresSide(null)} centered>
              <ModalHeader toggle={() => setGoleadoresSide(null)}>
                Goleadores · {teamName(goleadoresTeamId)}
              </ModalHeader>
              <ModalBody>{goleadoresSide && renderGoalEditor(goleadoresSide)}</ModalBody>
              <ModalFooter>
                <Button className="btn-arhock btn-sm my-0" onClick={() => setGoleadoresSide(null)}>
                  Listo
                </Button>
              </ModalFooter>
            </Modal>
          </>
        ) : match.jugado ? (
          <div className="d-flex mt-2" style={{ gap: "0.5rem" }}>
            <Button className="btn-arhock btn-sm my-0" onClick={startEdit}>
              Editar resultado
            </Button>
            <Button className="btn-arhock btn-sm my-0" onClick={quitar}>
              Quitar resultado
            </Button>
          </div>
        ) : (
          <Button className="btn-arhock btn-sm mt-2 mb-0" onClick={startEdit}>
            Cargar resultado
          </Button>
        )}
      </div>
    </div>
  );
}

// Pantalla final: tabla de posiciones (arriba) + partidos de la fecha para cargar
// resultados (abajo).
export default function ResultsScreen({
  zone,
  date,
  teams,
  headerTitle,
  torneoNombre,
  torneoAno,
  numeroFecha,
  onMatchChange,
  onBack,
}) {
  // Primero por cancha y, dentro de cada cancha, por horario.
  const partidosOrdenados = [...date.matches].sort(porCanchaYHora);
  const [exportando, setExportando] = useState(false);

  // PDF con la tabla de posiciones a esta fecha y los resultados de sus partidos.
  const exportarPdf = async () => {
    setExportando(true);
    try {
      await generarResultadosFecha({
        torneoNombre,
        torneoAno,
        zona: zone,
        fecha: date,
        numero: numeroFecha,
        teams,
      });
    } catch (ex) {
      toastBigError("No se pudo generar el PDF", "Intentá de nuevo en un momento.");
    } finally {
      setExportando(false);
    }
  };

  return (
    <div className="screen-slide">
      <NavHeader title={headerTitle} subtitle={zone.name} onBack={onBack} />

      <div className="d-flex justify-content-end mb-3">
        <Button className="btn-arhock btn-sm my-0" onClick={exportarPdf} disabled={exportando}>
          {exportando ? "Generando..." : "Exportar PDF"}
        </Button>
      </div>

      <div className="card mb-3">
        <div className="card-body">
          <div className="section-title">Tabla de posiciones</div>
          <StandingsTable zone={zone} upToFechaId={date.id} teams={teams} />
        </div>
      </div>

      <div className="sub-section-title">Partidos</div>
      {partidosOrdenados.length === 0 ? (
        <p className="text-muted text-center py-2 my-0" style={{ fontSize: "0.875rem" }}>
          No hay partidos en esta fecha.
        </p>
      ) : (
        partidosOrdenados.map((m) => (
          <MatchResultRow
            key={m.id}
            match={m}
            teams={teams}
            attendance={date.attendance || {}}
            onChange={onMatchChange}
          />
        ))
      )}
    </div>
  );
}
